import React, {useEffect, useRef} from 'react';
import Modal from '@mui/material/Modal';
import LightGreenSpinner from '../components/lightGreenSpinner';
import { inject, observer } from "mobx-react";
import Box from '@mui/material/Box';
import { DateTime } from "luxon";
import { repeatingLoyaltyRewards, loyaltyPaidOnce, consumeLoyaltyRewards } from '../utils/campaignTargeting';
import {DEFAULT_EMAIL, API_URL} from "../assets/globalConstants";
import {pickLanguageWord} from '../utils/pickLanguageWord.js';
import { constructMyDataForDeltio, issueDeltio, calculateVatsExpanded, readDeltioParts, stampDeltioItems,
  pushWindowsLine
 } from "../utils/print-service.js";
import { v4 as uuidv4 } from 'uuid';
import { toJS } from 'mobx';
import groupBy from 'lodash/groupBy';

// Translations
import _useTranslate from '../hooks/_useTranslate';

import * as uniq from 'lodash/uniq';

//----> VIVA REDIRECT IDEMPOTENCY
// The Viva Smart Checkout rail commits the order from /viva/success, rehydrated from the
// fdwa_orderItem/fdwa_state snapshot Checkout persisted before the redirect. A reload, a restored
// tab or a second open of the same success URL used to replay that snapshot in full: a second
// order (eshop), a second deltio (table) and a second loyalty burn. One small record per Viva
// orderCode (the `s` param) says how far its commit got:
//   'sending'   - the commit started; it may or may not have landed. A reload RE-SENDS it: the
//                 server dedups it (orders: dedupe-web-payment; tables: _clientItemKey/_clientPayKey),
//                 and a charged payment with no order is the worse failure.
//   'committed' - the order write returned. Never write it again.
//   'done'      - the loyalty/bonus settlement ran too.
// Per-browser by nature; the server guard covers everything else. Every access is try/catch -
// storage can be disabled, and a failed read falls back to the old behaviour.
const VIVA_COMMITS_KEY = 'fdwa_viva_commits';
const VIVA_COMMIT_TTL_MS = 60 * 24 * 60 * 60 * 1000; //longer than any tab a browser restores
const readVivaCommits = () => {
  try{
    const all = JSON.parse(localStorage.getItem(VIVA_COMMITS_KEY) || '{}');
    return (all && typeof all === 'object' && !Array.isArray(all)) ? all : {};
  }catch(e){ return {}; }
};
export const vivaCommitStage = key => {
  if(!key) return null;
  return readVivaCommits()[`${key}`.trim()]?.stage || null;
};
export const markVivaCommit = (key, stage) => {
  if(!key) return;
  try{
    const now = new Date().getTime();
    const all = readVivaCommits();
    for(const k of Object.keys(all)) if(!(now - (+all[k]?.at || 0) < VIVA_COMMIT_TTL_MS)) delete all[k];
    all[`${key}`.trim()] = { stage, at: now };
    localStorage.setItem(VIVA_COMMITS_KEY, JSON.stringify(all));
  }catch(e){ /* storage disabled - the server-side dedup still holds */ }
};
// Retire the redirect snapshot ONLY if it belongs to the Viva payment this modal just committed.
// The Checkout (cash/PayPal/Stripe) modal passes no key and never touches it: a cash order placed
// in another tab must not erase a Viva payment still in flight (its return would find no snapshot
// and the charged customer would get no order).
export const retireVivaSnapshot = key => {
  if(!key) return;
  try{
    const st = JSON.parse(localStorage.getItem('fdwa_state') || 'null');
    if(`${st?.orderCode ?? ''}`.trim() !== `${key}`.trim()) return;
    localStorage.setItem('fdwa_orderItem', JSON.stringify({}));
    localStorage.setItem('fdwa_state', JSON.stringify({}));
  }catch(e){ /* storage disabled - the commit marker still holds */ }
};
//<---- VIVA REDIRECT IDEMPOTENCY

const style = {
  position: 'absolute',
  width: {
    xs: '100%',
    sm: '80%', 
    md: 300,  
  },
  height: 270,
  top: {xs: 60, sm: '50%'},
  left: {xs: 0, sm: '50%'},
  transform: {sm: 'translate(-50%, -50%)'},   
  bgcolor: 'background.paper',
  border: '2px solid var(--border-color)',
  borderRadius: 4,
  boxShadow: 24,
 
};

const LoadingOrderModal = ({visible, whenSent, whenError, total, commitKey, feathersStore})=> {

  let common = _useTranslate(feathersStore.language, "common");
  const cartItems = JSON.parse(localStorage.getItem('fdwa_cart'));
  const masterLanguage = feathersStore.settings.masterLanguage;

  // Guards the one-shot commit below - see the effect for why a ref and not state.
  const sentRef = useRef(false);

  useEffect(() => {
   // An order whose grand total legitimately reaches 0.00 (a loyalty reward or a voucher covering
   // the whole cart) is a REAL order, not a missing one. The gate used to be a bare `total`
   // truthiness test, so such an order never committed and the customer sat on the spinner
   // forever. Card rails are protected upstream (Stripe rejects amount <= 0) but CASH reaches
   // here. Test that a NUMERIC total is present, not that it is non-zero.
   const hasTotal = total !== null && total !== undefined && total !== "" && Number.isFinite(+total);
   if(!(visible && hasTotal && feathersStore?.settings && (feathersStore?.tableId?.length > 0))) return;

   // ONCE per opening. `total` is recomputed on every render and `settings` is a live-synced
   // observable, so this effect re-fires while the modal is open - and each re-fire used to append
   // the same order to the table again. The ref is cleared when the modal closes (below).
   if(sentRef.current) return;

   // NOTE - deliberately NOT a blocking gate. An earlier revision refused to commit a card order
   // that carried no _webNew paidBy row. It was wrong twice over:
   //  - it read paymentMethod off the MERGED order, and the append spreads the live table order
   //    over it (Checkout.js:986), so a table whose earlier round was card-paid stamps 'PayPal'
   //    onto every later order - silently refusing legitimate CASH rounds for the rest of the
   //    table's life;
   //  - refusing a CHARGED order loses a paid order outright, which this codebase already rules
   //    out on purpose (Checkout.js:788-790: commit and mark, never lose a paid order).
   // Committing without a capture is prevented at the SOURCE instead: the paid-marks and the
   // paidBy row are both gated on `charged` in Checkout.sendOrder, so an uncaptured order can no
   // longer masquerade as paid. This only warns, so the case is visible in the console if it ever
   // does occur.
   const _order = feathersStore.orderItem || {};
   if(feathersStore.tableId !== 'eshop' && _order.paymentMethod === 'PayPal'
      && !(_order.paidBy || []).some(p => p?._webNew && +p?.paidSum > 0)){
     console.warn('[loadingOrderModal] card-rail order committing with no _webNew paidBy row —',
       'either an inherited paymentMethod from an earlier card round, or an uncredited capture.');
   }

   sentRef.current = true;
   sendOrder();
  }, [visible, total, feathersStore?.settings, feathersStore?.tableId]);

  // Re-arm only on a genuine close, so a retry after an error can commit.
  useEffect(() => {
    if(!visible) sentRef.current = false;
  }, [visible]);

  //----------> LOYALTY

  //The gift lines that shipped WITH THIS ORDER, read off the live cart. Must be called BEFORE
  //emptyCart(), which ends in resetOrderItem() and sets orderItem to {}.
  const collectBonusBurnCodes = () => (feathersStore.orderItem?.items || [])
    .filter(it => it?.product_free && it?._bonusRewardCode)
    .map(it => it._bonusRewardCode);

  //What this order actually took off the bill in loyalty: the capped figure the checkout wrote into
  //discountsArray as the Loyalty row. Read before emptyCart() for the same reason as above.
  const collectLoyaltyUsed = () => (feathersStore.orderItem?.discountsArray || [])
    .filter(d => d?.label === 'Loyalty')
    .reduce((a, d) => a + (+d?.value || 0), 0);

  const addNewRewards = async(_redeemedBonusCodes = [], _loyaltyUsed) => {//applied at the end of sendOrder
    if(feathersStore?.user?.email !== DEFAULT_EMAIL){ 
      //Accrual inputs first, and allowed to fail. This runs AFTER the order committed: a throw here used
      //to land in sendOrder's catch as "connection issue" over a placed order and - worse - skipped the
      //write below, so the loyalty this order SPENT was never booked. A missed accrual heals itself (the
      //next order recomputes cycles from history); a missed spend does not.
      let _campaigns = [];
      let userOrders = [];
      try{
        const _coupons = await feathersStore.coupons();
        _campaigns = _coupons?.filter(coupon =>  coupon?.active && (coupon?.completion > 0)) || [];
        //HOISTED out of the campaign loop: one archive-union fetch (~14 months), not one per campaign.
        userOrders = _campaigns.length > 0 ? await feathersStore.loyaltyOrdersPerUser() : [];
      }catch(error){
        console.log('loyalty accrual skipped', error);
        _campaigns = [];
        userOrders = [];
      }
      //Start from the list AS STORED, read after every await above, so only synchronous work sits between
      //this read and the write below. The in-memory copy trails writes by this customer's other devices;
      //this order's spend is a delta on the CURRENT balance - if another device already spent the reward
      //this order claimed, consumeLoyaltyRewards moves on to the next pending one. The server merges what
      //we send into the stored list anyway (users.js mergeLoyaltyRewards), so a failed read falls back to memory.
      const _storedUser = feathersStore.user?._id
        ? await feathersStore.app.service('users').get(feathersStore.user._id).catch(() => null)
        : null;
      let loyaltyRewardsClone = toJS((_storedUser || feathersStore.user)?.loyaltyRewards ?? []);
      //This used to stamp applied:true on EVERY reward unconditionally, so a Bonus cycle was spent
      //even when its gift line never shipped - the gift product had been deleted, or the single
      //getProduct() threw - and the customer lost a gift they had earned and never received.
      //A Bonus reward now burns only if its gift actually made it into THIS order; everything else
      //Money rewards are spent just below, by what this order actually used.
      for(let reward of loyaltyRewardsClone){
        if(reward?.bonus && _redeemedBonusCodes.includes(reward.code)){
          Object.assign(reward, {applied: true});
        }
      };
      //Money rewards: spend only what this order took off the bill, oldest first, and leave any
      //remainder pending. They used to be stamped applied after ANY order, whatever the bill
      //absorbed - see consumeLoyaltyRewards.
      consumeLoyaltyRewards(loyaltyRewardsClone, _loyaltyUsed);
     
      const appliedCampaigns = loyaltyRewardsClone?.map(r => r.code);

      const now = DateTime.now().setZone(feathersStore.timezone());
      //_campaigns and userOrders are fetched at the top, before the stored list is read
     
      for(let campaign of  _campaigns){
        if(campaign.repeatable || !loyaltyPaidOnce(campaign, appliedCampaigns)){   //a repeating campaign pays per cycle, see below   
          let valid = false;   
          if(campaign?.endDate){
            if(DateTime.fromISO(campaign.endDate).endOf('day') > now)valid = true;
          }else{
            if(DateTime.fromISO(campaign.startDate).plus({days : campaign.duration}).endOf('day') > now)valid = true;
          }
          if(valid){

            if(campaign.type === 'Bonus'){
              //Bonus = buy-N-get-1-free: count qualifying PAID pieces (campaign.products scope,
              //excluding gift/free lines) across non-deleted orders since startDate; repeatable per cycle of N.
              const scope = campaign.products || [];
              let pieces = 0;
              for(const o of (userOrders || [])){
                if(o?.deletedOrder) continue;
                if(!(DateTime.fromMillis(o.createdAt) > DateTime.fromISO(campaign.startDate))) continue;
                for(const it of (o.items || [])){
                  if(it?.product_free) continue;
                  if(scope.includes(it.product_id)) pieces += +it.product_quantity || 0;
                }
              }
              const cyclesEarned = +campaign.completion > 0 ? Math.floor(pieces / +campaign.completion) : 0;
              for(let cycle = 1; cycle <= cyclesEarned; cycle++){
                const cycleCode = `${campaign.code}-bonus-${cycle}`;
                if(!appliedCampaigns?.includes(cycleCode)){
                  loyaltyRewardsClone.push({
                    code: cycleCode, campaignCode: campaign.code, type: 'Bonus', bonus: true,
                    giftProductId: campaign.giftProductId, freeQty: +campaign.reward || 1,
                    promotionText: campaign.promotionText, value: 0, applied: false
                  });
                }
              }
            }else if(campaign.repeatable){
              //RECURRING: one fixed reward for every `completion` of spend since startDate, each cycle keyed
              //and paid exactly once however often this runs - see repeatingLoyaltyRewards.
              const spend = (userOrders || [])
                .filter(order => !order?.deletedOrder && DateTime.fromMillis(order.createdAt) > DateTime.fromISO(campaign.startDate))
                .reduce((a, order) => +a + (+order?.total || 0), 0);
              loyaltyRewardsClone.push(...repeatingLoyaltyRewards(campaign, spend, appliedCampaigns));
            }else if((userOrders
            ?.filter(order => !order?.deletedOrder && DateTime.fromMillis(order.createdAt) > DateTime.fromISO(campaign.startDate))
              .map(order => order?.total ?? 0)
              .reduce((a, b) => +a + +b, 0)) >= campaign?.completion){
              //Rounded to the cent: 15% of 25.50 is 3.825, and an unrounded value left a 0.01 remainder
              //pending after the checkout (which works in cents) had spent the whole reward.
              let value = +(+campaign.reward || 0).toFixed(2);
              if(campaign.type === "Percent"){
                value = +(campaign.completion * campaign.reward / 100).toFixed(2)
              }
              Object.assign(campaign, {applied: false, value});
              loyaltyRewardsClone.push(campaign);
            }           
          }
        }
      }     
      //The order has ALREADY committed when this runs; a throw from here reads as a failed ORDER in
      //sendOrder's catch and invites a duplicate. Log it instead.
      await feathersStore._patchUser(feathersStore?.user._id, {loyaltyRewards: loyaltyRewardsClone})
        .catch(error => console.log('loyalty rewards not saved', error));
    }
  }

  //-------------->

  const sendOrder = async() => {
    //Viva redirect replay guard - see VIVA REDIRECT IDEMPOTENCY at the top. success.js already refuses a
    //committed payment before this modal opens; this is the last line if it ever opens anyway.
    const _commitStage = vivaCommitStage(commitKey);
    if(_commitStage === 'committed' || _commitStage === 'done'){
      emptyCart();
      feathersStore.setDiscountsArray([]);
      whenSent();
      return;
    }
    markVivaCommit(commitKey, 'sending');
    //true once the server HOLDS the order. From then on a failure (printing, emptying the cart, the loyalty
    //patch) must not be reported as a failed order: the customer would retry an order that is already placed.
    let committed = false;
    try{
      if(feathersStore.tableId !== 'eshop' ){

        //----> BOOKING PRE-ORDER: attach the order to the BOOKING (not the table), so the table
        // stays visibly free until staff seat the guests and materialize it as a normal order.
        // No kitchen print and no deltio here - both happen at materialization/seating.
        const po = feathersStore.preOrderBooking;
        if(po?.tableId === feathersStore.tableId){
          const cached = toJS(feathersStore.orderItem);
          const stripWeb = ({ _webNew, ...rest }) => rest;
          const sessionItems = (cached.items || []).filter(i => i?._webNew).map((item, index) => ({ ...stripWeb(item), id: index }));
          const sessionPaidBy = (cached.paidBy || []).filter(p => p?._webNew).map(stripWeb);
          const sessionPayments = (cached.payments || []).filter(p => p?._webNew).map(stripWeb);
          const preOrderTotal = +(cached._webTotalDelta ?? cached.total ?? 0);
          // A Viva/online pre-order arrives here ALREADY paid: success.js recorded the charge as a
          // _webNew paidBy/payment. Derive unpaid from total minus what was actually prepaid so the
          // booking materializes as paid - otherwise staff would re-charge it. A cash pre-order has
          // no _webNew paidBy (prepaid = 0), so unpaid stays the full total, exactly as before.
          const prepaid = sessionPaidBy.reduce((sum, p) => sum + (+p?.paidSum || 0), 0);
          const preOrder = {
            ...cached,
            items: sessionItems,
            total: preOrderTotal,
            unpaid: +Math.max(0, preOrderTotal - prepaid).toFixed(2),
            paidBy: sessionPaidBy,
            payments: sessionPayments,
            status: 'Pending',
            deferredOrder: true,
            deferredTime: po.deferredTime
          };
          delete preOrder._webTotalDelta; delete preOrder._webUnpaidDelta; delete preOrder.ordering;
          await feathersStore.patchTable(feathersStore.tableId, {
            preOrderAttach: {
              bookingDay: po.bookingDay,
              bookingHour: po.bookingHour,
              bookingPhone: po.bookingPhone,
              bookingName: po.bookingName,
              preOrder
            }
          });
          committed = true;
          markVivaCommit(commitKey, 'committed');
          feathersStore.setPreOrderBooking(null);
          feathersStore.setTableId('eshop');
          //tombstone instead of removeItem — see Home.js booking-hour end: a bare removal would
          //let a second still-in-table-mode tab resurrect the ended session via the live-session rule.
          try{ localStorage.setItem('tableId', JSON.stringify({ value: 'eshop', expiry: new Date().getTime() + 6 * 60 * 60 * 1000 })); }catch(e){}
          //Captured BEFORE the cart is emptied - see collectBonusBurnCodes().
          const _burnedBonusCodes = collectBonusBurnCodes();
          const _loyaltyUsed = collectLoyaltyUsed();
          emptyCart();
          await addNewRewards(_burnedBonusCodes, _loyaltyUsed);
          markVivaCommit(commitKey, 'done');
          whenSent();
          return;
        }
        //<---- BOOKING PRE-ORDER END

        if(feathersStore.settings?.MY_DATA && !feathersStore.orderItem?.activeUid){
          feathersStore.setOrderItem({activeUid: uuidv4()}); //when the receipt is issued ==> activeUid => null        
        }
        feathersStore.setOrderItem({ordering: true})
        
        //----> subOrder START
        if(((feathersStore.settings?.country || 'el') !== 'el') || feathersStore.settings?.deltioAuto){
          const clonedItems = toJS(feathersStore.orderItem.items);
          let orderedProducts = [];     
          for (let item of clonedItems){
            if(!item?.subOrderId)orderedProducts.push(item);
          }
          if(orderedProducts.length > 0){
          const subOrderTotal = orderedProducts
            ?.map(item => item.product_free ? 0 : +item.product_totalPrice)
            .reduce((a,b) => a + b, 0)
            .toFixed(2);
          //Bundle-parts rule (bundle-vat-v2 §3/§6). ONE bounded parts read, only when a marked grouped line
          //is present - BEFORE the counter is read, so the counter's get->patch window stays as short as it
          //was. If it fails: Greek fiscal files nothing (the refused-Δελτίο path below; the dashboard issues
          //it later); a non-Greek tenant stores the round as today, stamped legacy.
          const deltioParts = await readDeltioParts(orderedProducts, feathersStore.findProductsForVatLookup);
          const oldDeltioCounter = await feathersStore.getDeltioCounter(); 
          let subOrderNumericId = +oldDeltioCounter.sequence_value + 1;
          orderedProducts.map(item =>  Object.assign(item, {subOrderId: subOrderNumericId}));
          //The stored lines are STAMPED COPIES, made after the last per-line field they carry (subOrderId
          //above; _clientItemKey is minted in Checkout). The order lines stay unstamped.
          let subOrder = {
            numericId: subOrderNumericId,
            issuer: feathersStore.user._id,
            issuerUsername: feathersStore.user?.username,
            subOrderTotal: parse_fix(subOrderTotal),
            createdAt: new Date().getTime(),
            tableId: feathersStore.tableId,
            tableName: feathersStore.orderItem._id,
            orderId: feathersStore.orderItem?._id,
            subOrderItems: stampDeltioItems(orderedProducts, deltioParts)
          };

          const vatsObj = calculateVatsExpanded(subOrder.subOrderItems, deltioParts.parts);
          Object.assign(subOrder,  vatsObj)
          await feathersStore.patchCounters("deltio", {sequence_value: parseInt(subOrderNumericId)});
          if(feathersStore.settings?.deltioAuto && feathersStore.isGreekFiscal()){

            const response = deltioParts.partsReadFailed ? null
              : await constructMyDataForDeltio(subOrder, 'orderTaking.component.ts', '513', subOrder.numericId, deltioParts.parts);
          
            if(response?.qrcode){
              Object.assign(subOrder, {response})
              const printPayload = await issueDeltio(subOrder, response); 
              Object.assign(subOrder, {printPayload});           
              let subOrders = toJS(feathersStore.orderItem?.subOrders ?? []);
              subOrders.push(subOrder);
              feathersStore.setOrderItem({subOrders});
            }else{
              //---> Rollback ---> Let it continue and the dashboard will take care
              subOrderNumericId = null;
              await feathersStore.patchCounters("deltio", {sequence_value: parseInt(oldDeltioCounter.sequence_value)});            
              
              //-------> 
            }
          }
          if((feathersStore.settings?.country || 'el') !== 'el'){
            let subOrders = toJS(feathersStore.orderItem?.subOrders ?? []);
            subOrders.push(subOrder);
            feathersStore.setOrderItem({subOrders});
          }
          for(let item of orderedProducts){
            Object.assign(item, {subOrderId: subOrderNumericId});
            const itemIndex = clonedItems.findIndex(orderItm => +orderItm.id === +item.id)
            if(itemIndex > -1)clonedItems.splice(itemIndex, 1, item);           
          };

          feathersStore.setOrderItem({items: clonedItems})
          }
        }
        //<------subOrder END

        //----> ATOMIC APPEND (server-side order lock)
        // Send only THIS web session's contribution (new items + payments + total delta) as a
        // thin atomicOrder payload; the SERVER re-reads the live order, mints the item ids,
        // merges and commits with a compare-and-swap on order._rev — so a concurrent
        // dashboard/PDA write can no longer be clobbered by a full-order replace. The
        // _clientItemKey/_clientPayKey idempotency keys make a network retry (or a Viva
        // success-page reload) credit items and payments exactly once.
        const cached = toJS(feathersStore.orderItem);

        //----> COUVER COMMIT HEAL: last-line defence for the TOCTOU window. The Checkout gate may
        // have passed because the couver sat on the LIVE table order, but staff can cash out and
        // clear the table while the customer dwells on the Viva page (or during the deltio
        // round-trips above) - the server would then seed a brand-new order from this append,
        // without a couver. If neither the live order nor this append carries the couver, inject it
        // here. Never blocks the commit: the payment may already be taken, so any failure
        // (unreadable couver product) just skips the heal.
        if(feathersStore.settings?.couver){
          try{
            const _couverId = feathersStore.settings?.couverId;
            const _newHasCouver = (cached.items || []).some(i => i?._webNew && i.product_id === _couverId);
            if(!_newHasCouver){
              const _live = await feathersStore.getTable(feathersStore.tableId);
              const _liveHasCouver = _live?.order?.items?.some(i => i.product_id === _couverId);
              if(!_liveHasCouver){
                const _cp = await feathersStore.getProduct(_couverId);
                //NaN would silently zero the whole session's totalDelta server-side ((+null||0)) — bail to the catch instead
                if(!Number.isFinite(+_cp?.retailPrice)) throw new Error('couver product has no usable price');
                const _size = _cp?.sizes?.find(s => s?.default);
                const _vatLabel = feathersStore.settings?.vats?.find(v => +v.id === +_cp.vat)?.label || 0;
                cached.items = [...(cached.items || []), {
                  _webNew: true,
                  _couverHeal: true, //reserved idempotency key below — must NOT consume a positional -iN key

                  createdAt: new Date(),
                  receipt: false,
                  product_id: _cp._id,
                  product_name: _cp.name,
                  product_nameTranslated: feathersStore._translateName(_cp),
                  product_size: _size || {},
                  product_image: `${API_URL}/images/${_cp?.images?.[0] || ""}`,
                  product_price: +_cp.retailPrice,
                  product_totalPrice: +_cp.retailPrice,
                  product_netPrice: +((+_cp.retailPrice) / (1 + _vatLabel / 100)).toFixed(2),
                  product_quantity: 1,
                  product_extraSum: 0,
                  features: [], ingredients: [], attributes: [],
                  product_printers: _cp.printers || [],
                  product_blockOtherPrinters: _cp.blockOtherPrinters || false,
                  product_kdsSections: _cp.kdsSections || [],
                  product_vat: _cp.vat,
                  ...feathersStore.lineMarker(false), //couver: dine-in priced, plain vat
                  product_stock: feathersStore.effectiveStock(_cp, _size),
                  product_handleStock: _cp.handleStock || false,
                  product_comments: "",
                  product_recipes: _size?.recipes?.length ? _size.recipes : (_cp.recipes ?? []),
                  isCommodity: _cp.isCommodity,
                  nameTranslations: _cp?.nameTranslations || [],
                  shortDescriptionTranslations: _cp?.shortDescriptionTranslations || []
                }];
                cached._webTotalDelta = +((+(cached._webTotalDelta ?? cached.total ?? 0)) + (+_cp.retailPrice)).toFixed(2);
              }
            }
          }catch(e){ console.log('Couver commit heal skipped:', e); }
        }
        //<---- COUVER COMMIT HEAL END

        const stripWeb = ({ _webNew, _couverHeal, id, ...rest }) => rest; // the server mints the ids
        const sendKey = cached.activeUid || feathersStore.orderCode || uuidv4();
        const seed = { ...cached };
        ['items','paidBy','payments','total','unpaid','_webTotalDelta','_webUnpaidDelta','ordering'].forEach(k => delete seed[k]);
        const update = {};
        if(cached.paymentMethod) update.paymentMethod = cached.paymentMethod;
        if(cached.activeUid) update.activeUid = cached.activeUid;
        if(cached.subOrders?.length > 0) update.subOrders = cached.subOrders;
        const data = await feathersStore.patchTable(feathersStore.tableId, { atomicOrder: {
          _mode: 'append',
          newItems: (cached.items || []).filter(i => i?._webNew)
            // Prefer the key Checkout minted per LINE of this submission. The positional fallback
            // below is only for an order already cached in a customer's browser from a build before
            // that change - it is the OLD scheme and carries its known flaw: `sendKey` is activeUid,
            // which lives for the whole table session, so a second round's -i0 collides with the
            // first round's -i0 and the server drops the new item as a duplicate. Keeping it means a
            // customer mid-checkout across the deploy still commits; new submissions never use it.
            // The healed couver keeps its RESERVED key: it is added after Checkout ran, so it has no
            // minted key, and a positional -iN would collide with a genuinely new item on a retry.
            .map((item, index) => ({ ...stripWeb(item), _clientItemKey: item._couverHeal ? `${sendKey}-couverheal` : (item._clientItemKey || `${sendKey}-i${index}`) })),
          // Prefer the key success.js minted per CHARGE. The positional fallback carries the same
          // flaw described above for items, and here it actually fired: the `_webNew` filter keeps
          // only THIS round's row, so `index` restarts at 0 while `sendKey` (activeUid) is constant
          // for the table session — every payment round re-minted `${activeUid}-pb0`, the server
          // dedup dropped rounds 2..N, and unpaid (derived from paidBy alone) never moved. It is
          // kept only for an order cached in a customer's browser from a build before that change.
          newPaidBy: (cached.paidBy || []).filter(p => p?._webNew)
            .map((p, index) => ({ ...stripWeb(p), _clientPayKey: p._clientPayKey || `${sendKey}-pb${index}` })),
          newPayments: (cached.payments || []).filter(p => p?._webNew)
            .map((p, index) => ({ ...stripWeb(p), _clientPayKey: p.transactionId || `${sendKey}-py${index}` })),
          totalDelta: +(cached._webTotalDelta ?? cached.total ?? 0),
          ordering: true,
          branch: cached.branch,
          seed,   // order scaffold (status/KDS flags/customer fields) — server uses it only for a fresh table order
          update  // fields that win over the server copy even on an existing order
        }});
        //<---- ATOMIC APPEND END
        committed = true;
        markVivaCommit(commitKey, 'committed');
        await printTableOrder(feathersStore.orderItem._id, feathersStore.orderItem.bookingDate, data.order.numericId)
      }else{
        if(feathersStore.settings?.MY_DATA)feathersStore.setOrderItem({activeUid: uuidv4()}); //used for mydata payment        
        await feathersStore.createOrder(feathersStore.orderItem); 
        committed = true;
        markVivaCommit(commitKey, 'committed');
      } 
      //Captured BEFORE the cart is emptied - see collectBonusBurnCodes().
      const _burnedBonusCodes = collectBonusBurnCodes();
      const _loyaltyUsed = collectLoyaltyUsed();
      emptyCart();
      //Clear the applied voucher with the cart. It is an ABSOLUTE amount computed against the cart it
      //was typed into, and nothing else ever cleared it - so on a permanent voucher the next order
      //silently reused the previous order's euro figure (order 1: EUR40 -> -EUR4.00; order 2: EUR10,
      //code not re-typed -> still -EUR4.00, a 40% discount). Cleared here, after the order has
      //committed, so a failed send still leaves the customer's voucher applied for the retry.
      feathersStore.setDiscountsArray([]);
      await addNewRewards(_burnedBonusCodes, _loyaltyUsed);
      markVivaCommit(commitKey, 'done');
      whenSent();
    }catch(error){
      if(committed){
        console.log('[loadingOrderModal] post-commit step failed - the order IS placed', error);
        try{ emptyCart(); }catch(e){ console.log(e); } //idempotent; may not have run yet
        feathersStore.setDiscountsArray([]);
        whenSent();
        return;
      }
      console.log(error)
      //The server verifies a card payment at Viva before it books the order (feathers-server
      //pos/viva-checkout.js). 402: Viva said no - nothing was paid for this order. 409/503: not confirmed
      //YET - the charge may be real, so nothing here is cleared: the commit stage stays 'sending' and the
      //Viva success page offers Retry (its whenError reads the error).
      if(error?.code === 402){
        alert(common.paymentNotVerified || "Your card payment could not be verified, so the order was not placed.");
        whenError(error);
        return;
      }
      if(error?.code === 409 || error?.code === 503 || error?.data?.retryable){
        if(!commitKey) alert(`${common.connectionIssue}`);
        whenError(error);
        return;
      }
      //A refused order is NOT a connection issue. The server can legitimately reject a create -
      //today only the first-order coupon hook does, when the customer is not entitled to the row
      //the cart is carrying - and showing the generic network message left the customer staring at
      //an order that would never go through, with no idea why.
      const refused = error?.code === 403 || error?.code === 400
        || error?.name === 'Forbidden' || error?.name === 'BadRequest';
      const couponRefused = refused && /coupon/i.test(error?.message || '');
      if(couponRefused){
        //Drop the offending row, or every retry refuses for the same reason and the customer is
        //stuck for good. The cart itself is untouched, so they can simply order again.
        feathersStore.setDiscountsArray([]);
        alert(`${common.voucherFirstOrderOnly}`);
      }else if(refused){
        alert(`${error.message}`);
      }else{
        alert(`${common.connectionIssue}`);
      }
      whenError(error);
    }    
  }  

  const checkLanguage = printerLang => {

    //----> In PDA there will be examined the feathersStore.settings.masterLanguage and the language 
    // of the printer only and not the feathersStore.language (it is only for UI).

    return printerLang ? printerLang : masterLanguage;
  }

  const checkAr = (printerLang) => {
    if(printerLang === "ar")return true;
    else if(masterLanguage === "ar")return true;
    else return false;
  }

  const constructAttributesList = (attributesArray, printer, printerLang, thermalFonts) => {
    let attributesList = "";
    for(let val of attributesArray){
      attributesList += 
      (printer.nonEpos ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>` : "") +
      val.name + " " + (val?.quantity > 1 ? ("x" + val.quantity) : "") + (printer.nonEpos ? "</text-line>" : "&#10;")
    }
    return attributesList;
  }

  const findCourse = (key, printerLang) => {
    switch (key){
      case '0': return pickLanguageWord(printerLang, 'first');
      case '1': return pickLanguageWord(printerLang, 'second');
      case '2': return pickLanguageWord(printerLang, 'third');
      case '3': return pickLanguageWord(printerLang,'fourth');
      case '4': return pickLanguageWord(printerLang, 'fifth');
      case '5': return pickLanguageWord(printerLang, 'sixth');
      case '6': return pickLanguageWord(printerLang, 'seventh');
      case '7': return pickLanguageWord(printerLang, 'eighth');
      case '19': return pickLanguageWord(printerLang, 'other');
      default: return "";
    }
  }

  const printTableOrder = async(tableName, bookingDate, numericId) => {
    const thermalFonts = feathersStore.settings?.thermalFonts ?? false;
    let finalOrderItems = [...cartItems];
    const date = new Date();
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().padStart(2, "0");
    const localDate = day + '/' + month + '/' + year

    // For grouped products, fetch sub-product printers and build union map
    const subProductIds = [];
    for (const item of finalOrderItems) {
      ((item.product_recipes || []).filter(r => r.isProduct && r.productId))
        .forEach(r => subProductIds.push(r.productId));
    }
    const subProductPrinterMap = {};
    if (subProductIds.length > 0) {
      const uniqueIds = [...new Set(subProductIds)];
      try {
        const subProducts = await feathersStore.findProductsPrinters(uniqueIds);
        subProducts.forEach(sp => subProductPrinterMap[sp._id] = sp.printers || []);
      } catch(e) {}
    }

    // For blockOtherPrinters, resolve section printers from the table
    let sectionPrinterNames = [];
    try {
      const table = await feathersStore.getTable(feathersStore.tableId);
      sectionPrinterNames = feathersStore.settings?.restaurantSections?.sections
        ?.find(s => s.name === table?.restaurantSection)?.printers?.map(prn => prn?.name) || [];
    } catch(e) {}

    for(let printer of feathersStore?.settings?.printers.filter(p => p.enabled)){
      const printerLang = checkLanguage(printer?.useLanguage && printer?.language ? printer.language : "");
      let itemsList = "";
      let windowsLines = [];
      let printerItems = finalOrderItems.filter(o => {
        const subRecipes = (o.product_recipes || []).filter(r => r.isProduct && r.productId);
        let hasPrinter;
        if (subRecipes.length > 0) {
          hasPrinter = subRecipes.some(r =>
            (subProductPrinterMap[r.productId] || []).some(p => p.name === printer.name));
        } else {
          hasPrinter = o.product_printers?.map(prn => prn.name).includes(printer.name);
        }
        if (!hasPrinter) return false;
        if (o.product_blockOtherPrinters && sectionPrinterNames.length > 0) {
          return sectionPrinterNames.includes(printer.name);
        }
        return true;
      });
      if(printerItems?.length > 0){

        if(printer?.windows || printer?.raster){
          pushWindowsLine(windowsLines, false, "center",
            `${localDate} ${date.toLocaleTimeString('en-GB')}`, 15 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), true);
          pushWindowsLine(windowsLines, false, "center",
            `${pickLanguageWord(printerLang, "table")}: ${tableName}`, 16 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), true, true);
          pushWindowsLine(windowsLines, false, "center", `${pickLanguageWord(printerLang, "newOrder")}  QRCODE`, 15 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
          pushWindowsLine(windowsLines, false, "center", `${pickLanguageWord(printerLang, "num")}: ${numericId}`, 15 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), true);
        }

         //---> course START
        printerItems.forEach(itm => {
          if((itm?.product_course?.length < 1) || (itm?.product_course === 'null') 
            || (itm?.product_course === null) || (itm?.product_course === undefined))Object.assign(itm, {product_course: '20'});
          return itm;
        })
        printerItems.sort((a,b) => a?.product_course - b?.product_course);
        let groupedItems = groupBy(printerItems, 'product_course');
        for (const [key, value] of Object.entries(groupedItems)) {
          if(printer?.nonEpos && value?.length > 0 && key !== '20'){
            itemsList = itemsList + `<text-line></text-line>
              <align mode="center">
              <bold><text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${findCourse(key, printerLang)} ${pickLanguageWord(printerLang, 'course')}</text-line>
              </align>
              </bold>`;

            if(printer?.windows || printer?.raster) pushWindowsLine(windowsLines, false, "center",
              `${findCourse(key, printerLang)} ${pickLanguageWord(printerLang, 'course')}`, 15 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), true);
          }
          //----> course END

        for (let item of value){
          let extraList = "";
          let ingredientList = "";
          let attributeList = "";
          const weightedString = item.product_weightedQuantity > 0 ? `${item.product_weightedQuantity * 1000} gr` : ""
          const productTotalPrice = item.product_free ? "0.00" : `${parse_fix(item.product_totalPrice)}`;
          const subproductNames = (item?.product_recipes || []).filter(r => r.isProduct).map(r => feathersStore.translateFeature(r, printerLang)).join(' · ');

          if(printer?.windows || printer?.raster){
            pushWindowsLine(windowsLines, false, "left",
              `${item.product_quantity} X ${feathersStore.translateProductName(item, printerLang)} ${feathersStore.translateFeature(item?.product_size, printerLang) || ""} ${weightedString}` +
                (feathersStore.settings?.printPrices ? ` ${feathersStore.formatMoney(productTotalPrice)}` : ""), 16 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), false, true);
            if(subproductNames){
              pushWindowsLine(windowsLines, false, "left", subproductNames, 13 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            }
            if(feathersStore.settings?.printDescription && item?.product_shortDescription?.length > 0){
              pushWindowsLine(windowsLines, false, "left", `${feathersStore.translateShortDescription(item, printerLang)}`, 14 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            }
          }

          if(item?.attributes){    
            let attributesMap = new Map();
            for(let attribute of item.attributes){
              if(!attributesMap.has( feathersStore.translateAttributeTitle(attribute, printerLang))){
                attributesMap.set( feathersStore.translateAttributeTitle(attribute, printerLang), 
                  new Array({
                    name:  feathersStore.translateAttributeName(attribute, printerLang),
                    quantity: attribute?.quantity || 0
                  }));
              }else{
                let valueArray = attributesMap.get( feathersStore.translateAttributeTitle(attribute, printerLang));
                valueArray.push({
                  name:  feathersStore.translateAttributeName(attribute, printerLang),
                  quantity: attribute?.quantity || 0
                });
                attributesMap.set( feathersStore.translateAttributeTitle(attribute, printerLang), valueArray);
              }
            }
            for(let entry of attributesMap.entries()){
              attributeList +=   
                (printer.nonEpos ? "<text-line></text-line>" : "&#10;") +
                (printer.nonEpos ? `<bold><text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>` : "") +
                entry[0] + (printer.nonEpos ? "</text-line></bold>" : "&#10;") +
                constructAttributesList(entry[1], printer, printerLang, thermalFonts);
              if(printer?.windows || printer?.raster){
                pushWindowsLine(windowsLines, true, "left", `${entry[0]} `, 13 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
                for(let val of entry[1]){
                  pushWindowsLine(windowsLines, false, "left", `${val.name} ${(val?.quantity > 1 ? ("x" + val.quantity) : "")}`, 13 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
                }
              }
            }        
          }; 

         if(item?.features?.length > 0){
            if(printer?.windows || printer?.raster) pushWindowsLine(windowsLines, true, "left", `${pickLanguageWord(printerLang, "extra")}`, 13 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            for(let feature of item.features){extraList +=
              (printer.nonEpos ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>` : "") +
              feathersStore.translateFeature(feature, printerLang) +
              (printer.nonEpos ? "</text-line>" : "&#10;");
              if(printer?.windows || printer?.raster) pushWindowsLine(windowsLines, false, "left", `${feathersStore.translateFeature(feature, printerLang)}`, 14 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            };
          }

         if(item?.ingredients?.length > 0){
            if(printer?.windows || printer?.raster) pushWindowsLine(windowsLines, true, "left", `${pickLanguageWord(printerLang, "without")}`, 13 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            for(let ingredient of item.ingredients){ingredientList +=
              (printer.nonEpos ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>` : "") +
              feathersStore.translateFeature(ingredient, printerLang) +
              (printer.nonEpos ? "</text-line>" : "&#10;");
              if(printer?.windows || printer?.raster) pushWindowsLine(windowsLines, false, "left", `${feathersStore.translateFeature(ingredient, printerLang)}`, 14 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            };
          }

          if(printer?.windows || printer?.raster){
            if(item.product_comments?.length > 0){
              pushWindowsLine(windowsLines, false, "left", `${pickLanguageWord(printerLang, "comments").toLocaleUpperCase()}: ${item.product_comments}`, 16 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0));
            }
            pushWindowsLine(windowsLines, false, "left", "", 10 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), true);
          }
          
          
         if(printer.nonEpos){
            itemsList = itemsList +
            `<text-line></text-line><bold><text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${item.product_quantity} X ${feathersStore.translateProductName(item, printerLang)} ${feathersStore.translateFeature(item?.product_size, printerLang)  ?? ""} ${weightedString} ${nonEposMoney(productTotalPrice)}</text-line></bold>` +
              (subproductNames ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${subproductNames}</text-line>` : "") +
              (feathersStore.settings?.printDescription && item?.product_shortDescription ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${feathersStore.translateShortDescription(item, printerLang)}</text-line>` : "") +
              (extraList ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${extraList}</text-line>` : "") +
              (attributeList ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${attributeList}</text-line>` : "") +
              (ingredientList ? `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "without")}: ${ingredientList}</text-line>` : "") +
              (item.product_comments ? `<bold><text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "comments").toLocaleUpperCase()}: ${item.product_comments}</text-line></bold><text-line></text-line>` : "")
          }else{
            itemsList = itemsList + `<text lang="en" em="true">${item.product_quantity} X ${feathersStore.translateProductName(item, printerLang)} ${feathersStore.translateFeature(item?.product_size, printerLang) ?? ""} ${weightedString} (${feathersStore.formatMoney(productTotalPrice)})&#10;</text>` +
              (subproductNames ? `<text lang="en" em="false" smooth="true">${subproductNames}&#10;</text>` : "") +
              (feathersStore.settings?.printDescription && item?.product_shortDescription ? `<text lang="en" em="false" smooth="true">${feathersStore.translateShortDescription(item, printerLang)}&#10;</text>` : "") +
              (extraList ? `<text lang="en" em="false" smooth="true">${extraList}&#10;</text>` : "") +
              (attributeList ? `<text lang="en" em="false" smooth="true">${attributeList}</text>` : "") +
              (ingredientList ? `<text lang="en" em="false" smooth="true">${pickLanguageWord(printerLang, "without")}: ${ingredientList}&#10;</text>` : "") +
              (item.product_comments ? `<text lang="en" em="true" smooth="true">${pickLanguageWord(printerLang, "comments").toLocaleUpperCase()}: ${item.product_comments}&#10;</text>` : "")
          } 
        } 
        }  

        let req =   
          '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">' +
            '<s:Body>' +
              '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
              '<text align = "center" />' +
              constructCompanyTitle() +
              '<feed unit="24"/>' + 
              `${thermalFonts ? '<text width="2"></text>' : ''}` +
              `<text lang="en" em="true">${pickLanguageWord(printerLang, "newOrder")} QRCODE&#10;</text>` +
              `<text lang="en" em="true">${pickLanguageWord(printerLang, "num")}: ${numericId}&#10;</text>` +
              '<feed unit="24"/>' +
              `<text lang="en" em="true">>${pickLanguageWord(printerLang, "table")}: ${tableName}&#10;</text>` +
              `<text lang="en" em="true">${pickLanguageWord(printerLang, "dateCut")}: ${localDate} ${date.toLocaleTimeString('en-GB')}&#10;</text>` +
              `${thermalFonts ? '<text width="1"></text>' : ''}` +
              `<text lang="en">------------------------------------------&#10;</text>` +
              `${thermalFonts ? '<text width="2"></text>' : ''}` +
              '<text align = "left" />' +
                itemsList +
                (feathersStore.orderItem?.comments ? `<text lang="en" em="true">${pickLanguageWord(printerLang, "comments").toLocaleUpperCase()}: ${feathersStore.orderItem.comments}&#10;</text>` : "") +
              '<feed unit="24"/>' +
              '<text align = "center" />' +
              `<text lang="en" em="false" smooth="true">${pickLanguageWord(printerLang, "total")}: ${feathersStore.formatMoney(parse_fix(total))}&#10;</text>` +
              `${thermalFonts ? '<text width="1"></text>' : ''}` +
              `<text lang="en">------------------------------------------&#10;</text>` +
              `${thermalFonts ? '<text width="2"></text>' : ''}` +
              `<text lang="en">qrcode&#10;</text>` +           
              '<feed unit="24"/>' +                
              '<cut/>' +
              '</epos-print>' +
            '</s:Body>' +
          '</s:Envelope>';
        let payload = {ip: printer.ip, devid: printer.devId, req};

        if(printer?.nonEpos){
         
          req =  
            '<?xml version="1.0" encoding="UTF-8"?>' +
            '<document>' +
              '<set-cp/>' +
              '<align mode="center">' +
                constructCompanyTitleNonEpos() +             
              '<line-feed />' +                             
              '<bold>' +
                `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "newOrder")} QRCODE: ${numericId}</text-line>` +
                `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "num")}: ${numericId}</text-line>` +
                '<line-feed />' +
                `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "table")}: ${tableName}</text-line>` +
                `<text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""}  ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "dateCut")}: ${localDate} ${date.toLocaleTimeString('en-GB')}</text-line>` + 
              '</bold>' +
              `<text lang="en">------------------------------------------</text>` +
              '</align>' +
              '<align mode = "left">' +
                itemsList +
                (feathersStore.orderItem?.comments ? `<bold><text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "comments").toLocaleUpperCase()}: ${feathersStore.orderItem.comments}</text-line></bold>` : "") +
              '</align>' +
              '<line-feed />' +
              '<align mode="center">' +
                `<bold><text-line ${checkAr(printerLang) ? 'lang = "ar"' : ""} ${thermalFonts ? 'size="1:0"' : ''}>${pickLanguageWord(printerLang, "total")}: ${nonEposMoney(parse_fix(total))}</text-line></bold>` +
                `<text-line>------------------------------------------</text-line>` +
                `<text-line ${thermalFonts ? 'size="1:0"' : ''}>qrcode</text-line>` +
              '</align>' +
              '<line-feed />' +                     
              '<paper-cut />' +
            '</document>' ;
           
          Object.assign(payload, {nonEpos: true, req})

          if(printer?.windows || printer?.raster){
            if(feathersStore.orderItem?.comments?.length > 0){
              pushWindowsLine(windowsLines, false, "left",
                `${pickLanguageWord(printerLang, "comments").toLocaleUpperCase()}: ${feathersStore.orderItem.comments}`,
                17 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), false);
            }
            if(feathersStore.settings?.printPrices){
              pushWindowsLine(windowsLines, true, "left",
                `${pickLanguageWord(printerLang, "total")}: ${feathersStore.formatMoney(parse_fix(total))}`,
                16 - (feathersStore?.settings?.orderWindowsPrintFontReduction ?? 0), true);
            }
          }
        }

        if(printer?.windows || printer?.raster){
          Object.assign(payload, {
            windows: !!printer?.windows,
            raster: !!printer?.raster,
            req: "",
            lang: checkAr(printerLang),
            windowsLines          
          })
        }

        try{
          await feathersStore.createLocalPrint(payload);
        }catch(err){console.log('Could not Print!', err)};    
      }
    }
  }

  const constructCompanyTitle = () => {
    return(
    `<text dw="true" dh="true" />`+
    `<text lang="en" em="true">${feathersStore.settings.companyName}&#10;</text>` +
    '<feed unit="24"/>' + 
    `<text dw="false" dh="false" />`+
    `<text lang="en" em="true">${feathersStore.settings.address}&#10;</text>` + 
    `<text lang="en" em="true">${feathersStore.settings.occupation}&#10;</text>` 
    )
  }

  const constructCompanyTitleNonEpos = () => {
    return(
      `<bold><text-line size="1:1">${feathersStore.settings.companyName}</text-line></bold>` +
      '<line-feed />' +
      '<bold>' +
        `<text-line>${feathersStore.settings.address}</text-line>` + 
        `<text-line>${feathersStore.settings.occupation}</text-line>` +
      '</bold>'
    )
  }

  const parse_fix = price => {
    return price ? parseFloat(price).toFixed(2) : 0;
  }

  //nonEpos money: the <set-symbol-cp> tag MUST survive — it is how the thermal print server
  //emits the symbol in the right codepage, so it cannot be swapped for a plain formatMoney
  //string. Only its PLACEMENT is conditional: prefix for GBP/USD (the same rule formatMoney
  //uses), suffix (today's byte-for-byte order) otherwise. Both nonEpos money sites go through
  //here so they cannot drift; mirrors nonEposMoney() as shipped in the mobile apps.
  const nonEposMoney = amount => {
    const code = String(feathersStore.settings?.currency || "EUR").toUpperCase();
    const symbolTag = `<set-symbol-cp>${feathersStore.currency}</set-symbol-cp>`;
    return (code === "GBP" || code === "USD") ? `${symbolTag}${amount}` : `${amount}${symbolTag}`;
  }

  const emptyCart = () => {    
    localStorage.setItem("fdwa_cart", JSON.stringify([]));
    //Commented out in c371901 (2025-08-08): success.js's rehydrate effect depended on localStorage.getItem('fdwa_orderItem'),
    //so clearing here re-fired it with '{}' and wiped tableId/currency/orderCode. That effect is mount-only now.
    //Scoped to the committed Viva payment - see retireVivaSnapshot.
    retireVivaSnapshot(commitKey);
    feathersStore.resetOrderItem();
    feathersStore.setCartLength(0);
    feathersStore.setOrderCode(null);
  }

  return (
    <section>
      <Modal 
        open={visible}        
        aria-labelledby="login-modal"
        aria-describedby="login-or-register"
      >
        <Box sx={style}>
          <div className="body-container">
            <h3>{common.sendOrder}</h3>
            <div>{common.pleaseWait}</div>
            <div className="modalRegisterFormContainer">
              <LightGreenSpinner              
                size = {110}
              />
            </div>
          </div>
        </Box>
      </Modal>
    </section>
  );
}

export default inject('feathersStore')(observer(LoadingOrderModal));
